import React, { useState, useEffect, createContext } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { useGetTeamsQuery } from 'common/store/modules/api/afl-api';
import {
    useGetBFAwardsLeaderboardQuery,
    useGetBrownlowBettingOddsQuery
} from 'common/store/modules/api/cfs-api';

import {
    leaderboardFilterSelector,
    leaderboardNavSelector
} from 'common/store/modules/brownlow-tracker/leaderboard/selectors';

import { LeaderboardTable } from './LeaderboardTable';

import { usePagination } from '../../../hooks/use-fetch-more-data';
import { useFilteredData } from '../../../hooks/use-search-filter';
import { useBettingVis } from 'common/react/hooks/use-betting-vis';
import { useInterval } from 'common/react/hooks/use-interval';

/**
 * @constant {number} PAGE_SIZE Number of items to show per page
 */
const PAGE_SIZE = 15;

/**
 * @constant {number} POLLING_DELAY How often to poll for new data
 */
const POLLING_DELAY = 30000; // 30 seconds

/**
 * @constant {number} LEADER_ROUND_DELAY How many rounds until we start
 * to highlight the leader
 */
const LEADER_ROUND_DELAY = 3;

export const SeasonContext = createContext('');
export const CurrentRoundContext = createContext('');

export const LeaderboardTableFetcher = ({ shouldHidePredicted, title }) => {
    const [data, setData] = useState([]);

    const nav = useSelector(leaderboardNavSelector);

    const seasons = useSelector(
        (state) =>
            state.seasons[`competition_${nav.competition.id}`]?.list ?? []
    );
    const season = seasons.find((item) => item.id === nav.season.id);
    const seasonPid = season?.providerId ?? '';

    const currentRoundNumber = season?.currentRoundNumber;

    // Fetch teams data
    const { data: teamsData } = useGetTeamsQuery(season?.id, {
        skip: !season?.id ? true : false
    });

    const teams = teamsData || [];
    const team = teams.find((item) => item.id === nav.team.id);
    const teamId = team?.providerId ?? '';

    // Fetch leaderboard data
    const {
        data: leaderboardData,
        refetch: refetchLeaderboardData,
        isFetching: isLeaderboardFetching
    } = useGetBFAwardsLeaderboardQuery({
        seasonId: seasonPid,
        teamId: teamId ?? ''
    });

    const leaderboardFilter = useSelector(leaderboardFilterSelector);
    let filteredData = useFilteredData(data, leaderboardFilter);

    // Use pagination hook to manage pagination and fetching more data
    const [
        shownData,
        fetchMoreData,
        currentPage,
        setShownData,
        setCurrentPage
    ] = usePagination(leaderboardData?.leaderboard, PAGE_SIZE);

    useEffect(() => {
        setCurrentPage(1);
    }, [season]); // Reset current page when season changes

    // Get the status of the leaderboard data
    const status = leaderboardData?.status
        ? PULSE.app.common.match.getMatchStatus(leaderboardData.status)
        : '';

    // Should we poll for data?
    const shouldPoll =
        status !== PULSE.app.common.CONSTANTS.MATCH_STATUS.COMPLETED;

    // set the shown data based on the filtered results
    useEffect(() => {
        setShownData(filteredData.slice(0, PAGE_SIZE * currentPage));
    }, [filteredData, setShownData, currentPage]);

    // Handle showing the leader based on match status and current round
    const [showLeader, setShowLeader] = useState(false);

    useEffect(() => {
        if (!team) {
            if (status === PULSE.app.common.CONSTANTS.MATCH_STATUS.LIVE) {
                // Check if more than 3 rounds
                if (
                    leaderboardData?.leaderboard?.[0]?.roundByRoundVotes
                        ?.length >= LEADER_ROUND_DELAY
                ) {
                    setShowLeader(true);
                }
            } else if (
                status === PULSE.app.common.CONSTANTS.MATCH_STATUS.COMPLETED
            ) {
                setShowLeader(true);
            }
        }
    }, [status, team, leaderboardData]);

    // Handle betting odds data
    const { data: bettingOddsData } = useGetBrownlowBettingOddsQuery(null, {
        pollingInterval: PULSE.app.common.CONSTANTS.TIME.ONE_MINUTE_IN_MS
    });

    const shouldShowBettingOdds =
        useBettingVis() &&
        status !== PULSE.app.common.CONSTANTS.MATCH_STATUS.COMPLETED &&
        bettingOddsData?.displayInApp === true &&
        bettingOddsData?.outcomes?.length > 0;

    // Poll for leaderboard data if live
    useInterval(() => {
        if (shouldPoll) {
            refetchLeaderboardData();
        }
    }, POLLING_DELAY);

    return (
        <SeasonContext.Provider value={season}>
            <CurrentRoundContext.Provider value={currentRoundNumber}>
                <LeaderboardTable
                    title={title}
                    shouldHidePredicted={shouldHidePredicted}
                    leaderboardData={leaderboardData}
                    setData={setData}
                    filteredData={filteredData}
                    shownData={shownData}
                    fetchMoreData={fetchMoreData}
                    isLeaderboardFetching={isLeaderboardFetching}
                    pageSize={PAGE_SIZE}
                    currentPage={currentPage}
                    showLeader={showLeader}
                    shouldShowBettingOdds={shouldShowBettingOdds}
                    bettingOddsData={bettingOddsData}
                />
            </CurrentRoundContext.Provider>
        </SeasonContext.Provider>
    );
};

LeaderboardTableFetcher.propTypes = {
    cssClass: PropTypes.string,
    shouldHidePredicted: PropTypes.bool,
    title: PropTypes.string
};
